import React, { useState, useMemo } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ScanSearch, Play, Loader2, Download, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, RotateCcw, Search, LayoutGrid, List,
} from 'lucide-react';

const ESTADO_STYLE = (estado) => {
  const e = (estado || '').toUpperCase();
  if (e === 'COMPLETED' || e === 'OK') return 'bg-emerald-100 text-emerald-800';
  if (e === 'FAILED' || e === 'REJECTED' || e === 'REVERSED') return 'bg-red-100 text-red-800';
  if (e === 'PENDING' || e === 'PROCESSING') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-200 text-slate-700';
};

const Persona = ({ titulo, p }) => {
  if (!p || (!p.cuit && !p.cbu && !p.nombre && !p.banco)) return null;
  return (
    <div className="text-xs">
      <div className="font-semibold text-slate-500 uppercase text-[10px] mb-0.5">{titulo}</div>
      {p.nombre && <div className="text-slate-800 font-medium">{p.nombre}</div>}
      {p.cuit && <div className="text-slate-600 font-mono">CUIT {p.cuit}</div>}
      {p.cbu && <div className="text-slate-600 font-mono">{p.cbu}</div>}
      {p.banco && <div className="text-slate-600">{p.banco}</div>}
    </div>
  );
};

const ResultCard = ({ r, idx }) => {
  const [open, setOpen] = useState(false);
  const proc = r.procesado;
  return (
    <Card className="p-4 bg-white" data-testid={`coelsa-result-${idx}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-mono text-sm font-bold text-slate-900" data-testid={`coelsa-codigo-${idx}`}>{r.codigo}</div>
          <div className="text-xs text-slate-500 mt-0.5">{r.mensaje || (r.encontrado ? 'Transacción encontrada' : 'Sin resultados')}</div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {!r.encontrado && (
            <Badge className="bg-slate-200 text-slate-700" data-testid={`coelsa-noencontrado-${idx}`}>No encontrado</Badge>
          )}
          {r.estado && <Badge className={ESTADO_STYLE(r.estado)} data-testid={`coelsa-estado-${idx}`}>{r.estado}</Badge>}
          {r.reintegrada && <Badge className="bg-purple-100 text-purple-800">Reintegrada</Badge>}
          {r.procesable
            ? <Badge className="bg-cyan-100 text-cyan-800" data-testid={`coelsa-procesable-${idx}`}>Procesable</Badge>
            : (r.encontrado && <Badge variant="outline" className="text-slate-500">No procesable</Badge>)}
        </div>
      </div>

      {r.encontrado && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
          <div><div className="text-[10px] uppercase text-slate-400">Tipo</div><div className="text-slate-800">{r.tipo || '-'}</div></div>
          <div><div className="text-[10px] uppercase text-slate-400">Monto</div><div className="text-slate-800 font-semibold">{r.monto || '-'}</div></div>
          <div><div className="text-[10px] uppercase text-slate-400">Fecha Transacción</div><div className="text-slate-800">{r.fecha_transaccion || '-'}</div></div>
          <div><div className="text-[10px] uppercase text-slate-400">Fecha Acreditación</div><div className="text-slate-800">{r.fecha_acreditacion || '-'}</div></div>
        </div>
      )}

      {r.encontrado && (r.origen?.cuit || r.destino?.cuit) && (
        <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-slate-100">
          <Persona titulo="Origen" p={r.origen} />
          <Persona titulo="Destino" p={r.destino} />
        </div>
      )}

      {proc && (
        <div className={`mt-3 rounded-md p-2 text-xs flex items-start gap-2 ${proc.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`} data-testid={`coelsa-procesado-${idx}`}>
          {proc.ok ? <CheckCircle2 size={14} className="mt-0.5" /> : <XCircle size={14} className="mt-0.5" />}
          <span><strong>{proc.ok ? 'Procesado' : 'No procesado'}:</strong> {proc.mensaje}</span>
        </div>
      )}

      {r.secciones && Object.keys(r.secciones).length > 0 && (
        <div className="mt-3">
          <button onClick={() => setOpen(o => !o)} data-testid={`coelsa-detalle-toggle-${idx}`}
            className="text-xs text-cyan-700 hover:text-cyan-900 flex items-center gap-1 font-medium">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Ver detalle completo de Waya
          </button>
          {open && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(r.secciones).map(([titulo, texto]) => (
                <div key={titulo} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                  <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">{titulo}</div>
                  <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-sans leading-snug">{texto.replace(new RegExp('^' + titulo + '\\s*', 'i'), '')}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const CoelsaTable = ({ items }) => {
  const [open, setOpen] = useState({});
  return (
    <Card className="bg-white overflow-hidden" data-testid="coelsa-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Id Coelsa</th>
              <th className="text-left px-3 py-2 font-semibold">Estado</th>
              <th className="text-left px-3 py-2 font-semibold">Tipo</th>
              <th className="text-right px-3 py-2 font-semibold">Monto</th>
              <th className="text-left px-3 py-2 font-semibold">Origen</th>
              <th className="text-left px-3 py-2 font-semibold">Destino</th>
              <th className="text-left px-3 py-2 font-semibold">F. Transacción</th>
              <th className="text-left px-3 py-2 font-semibold">Acción</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((r, idx) => (
              <React.Fragment key={`${r.codigo}-${idx}`}>
                <tr className="hover:bg-slate-50" data-testid={`coelsa-row-${idx}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-slate-800 whitespace-nowrap">{r.codigo}</td>
                  <td className="px-3 py-2">
                    {!r.encontrado
                      ? <Badge className="bg-slate-200 text-slate-700">No encontrado</Badge>
                      : <div className="flex flex-wrap gap-1">
                          {r.estado && <Badge className={ESTADO_STYLE(r.estado)}>{r.estado}</Badge>}
                          {r.reintegrada && <Badge className="bg-purple-100 text-purple-800">Reintegrada</Badge>}
                        </div>}
                  </td>
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.tipo || '-'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800 whitespace-nowrap">{r.monto || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {r.origen?.nombre || '-'}
                    {r.origen?.cuit && <div className="text-[10px] text-slate-400 font-mono">{r.origen.cuit}</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {r.destino?.nombre || '-'}
                    {r.destino?.cuit && <div className="text-[10px] text-slate-400 font-mono">{r.destino.cuit}</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.fecha_transaccion || '-'}</td>
                  <td className="px-3 py-2">
                    {r.procesado
                      ? <Badge className={r.procesado.ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}>{r.procesado.ok ? 'Procesado' : 'No procesado'}</Badge>
                      : (r.procesable
                        ? <Badge className="bg-cyan-100 text-cyan-800">Procesable</Badge>
                        : (r.encontrado ? <span className="text-slate-400">No procesable</span> : '-'))}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {r.secciones && Object.keys(r.secciones).length > 0 && (
                      <button onClick={() => setOpen(o => ({ ...o, [idx]: !o[idx] }))}
                        data-testid={`coelsa-row-toggle-${idx}`} className="text-slate-400 hover:text-slate-700">
                        {open[idx] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      </button>
                    )}
                  </td>
                </tr>
                {open[idx] && r.secciones && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={9} className="px-3 py-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(r.secciones).map(([titulo, texto]) => (
                          <div key={titulo} className="rounded-md border border-slate-200 bg-white p-2">
                            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">{titulo}</div>
                            <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-sans leading-snug">{texto.replace(new RegExp('^' + titulo + '\\s*', 'i'), '')}</pre>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const CoelsaTransacciones = () => {
  const [texto, setTexto] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [modo, setModo] = useState(null); // 'consultar' | 'procesar'
  const [vista, setVista] = useState('cards'); // 'cards' | 'lista'

  const codigos = useMemo(() => {
    const seen = new Set();
    const out = [];
    texto.replace(/,/g, '\n').split('\n').forEach(l => {
      const s = l.trim();
      if (s && !seen.has(s)) { seen.add(s); out.push(s); }
    });
    return out;
  }, [texto]);

  const ejecutar = async (procesar) => {
    if (codigos.length === 0) { toast.error('Pegá al menos un Id Coelsa'); return; }
    if (procesar && !window.confirm(`Vas a intentar PROCESAR ${codigos.length} transacción(es) en Waya. Esto ejecuta una acción real (reintegro/acreditación) sobre las que estén disponibles. ¿Continuar?`)) return;
    setLoading(true);
    setModo(procesar ? 'procesar' : 'consultar');
    setItems([]);
    setResumen(null);
    setProgress({ procesados: 0, total: codigos.length });
    try {
      const res = await api.post('/coelsa/consultar', { codigos, procesar });
      const jobId = res.data?.job_id;
      let job = null;
      for (let i = 0; i < 600; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const jr = await api.get(`/coelsa/job/${jobId}`);
        job = jr.data;
        setProgress({ procesados: job?.procesados || 0, total: job?.total || codigos.length });
        const parcial = job?.parcial || [];
        if (parcial.length) setItems([...parcial]);
        if (job?.estado === 'completado' || job?.estado === 'error') break;
      }
      if (job?.estado === 'error') {
        toast.error(`Error en Waya: ${job?.error || 'desconocido'}`);
      } else {
        const r = job?.resultado || {};
        setItems(r.items || job?.parcial || []);
        setResumen(r);
        toast.success(`${r.encontrados || 0} encontrada(s) de ${r.total || 0}${procesar ? ` · ${r.procesados_ok || 0} procesada(s)` : ''}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al consultar Waya');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const exportCSV = () => {
    if (items.length === 0) return;
    const cols = ['codigo', 'encontrado', 'estado', 'tipo', 'monto', 'reintegrada', 'fecha_reintegro',
      'fecha_transaccion', 'fecha_acreditacion', 'concepto', 'mensaje', 'procesable'];
    const personaCols = ['origen', 'destino'];
    const header = [...cols, 'origen_cuit', 'origen_nombre', 'destino_cuit', 'destino_nombre', 'procesado_ok', 'procesado_msg'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = items.map(r => [
      ...cols.map(c => esc(r[c])),
      esc(r.origen?.cuit), esc(r.origen?.nombre), esc(r.destino?.cuit), esc(r.destino?.nombre),
      esc(r.procesado?.ok), esc(r.procesado?.mensaje),
    ].join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coelsa_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="coelsa-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScanSearch size={28} className="text-cyan-600" />
            Validar / Procesar Coelsa
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Consultá el estado de transferencias en Waya por Id Coelsa y procesá las que estén disponibles.</p>
        </div>
        {items.length > 0 && (
          <Button variant="outline" onClick={exportCSV} data-testid="coelsa-export-btn">
            <Download size={16} className="mr-2" /> Exportar CSV
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panel de entrada */}
        <Card className="p-4 bg-white lg:col-span-1 h-fit lg:sticky lg:top-4">
          <label className="text-xs uppercase font-semibold text-slate-500">Ids Coelsa (uno por línea)</label>
          <Textarea
            rows={10}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={'Z0KV87947GDL3EQQ9PEYDX\n46YGOW9MGD7XKERM9EXD8J\n...'}
            className="font-mono text-xs mt-1"
            data-testid="coelsa-input"
            disabled={loading}
          />
          <div className="text-xs text-slate-500 mt-1">{codigos.length} código(s) detectado(s)</div>

          <div className="flex flex-col gap-2 mt-3">
            <Button onClick={() => ejecutar(false)} disabled={loading || codigos.length === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="coelsa-validar-btn">
              {loading && modo === 'consultar' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
              {loading && modo === 'consultar' ? (progress ? `Validando ${progress.procesados}/${progress.total}…` : 'Validando…') : `Validar (${codigos.length})`}
            </Button>
            <Button onClick={() => ejecutar(true)} disabled={loading || codigos.length === 0}
              variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="coelsa-procesar-btn">
              {loading && modo === 'procesar' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
              {loading && modo === 'procesar' ? (progress ? `Procesando ${progress.procesados}/${progress.total}…` : 'Procesando…') : `Procesar (${codigos.length})`}
            </Button>
          </div>

          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-[11px] text-amber-700">Solo se procesan las transferencias <strong>recibidas en estado "Completado"</strong> no impactadas en la wallet y las <strong>fallidas para reintegrar</strong>. Las ya procesadas no permiten acción.</p>
          </div>
        </Card>

        {/* Resultados */}
        <div className="lg:col-span-2 space-y-3">
          {(resumen || items.length > 0) && (
            <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="coelsa-resumen">
              <div className="flex flex-wrap gap-2 text-xs">
                {resumen && <Badge className="bg-slate-100 text-slate-700">Total: {resumen.total}</Badge>}
                {resumen && <Badge className="bg-emerald-100 text-emerald-800">Encontradas: {resumen.encontrados}</Badge>}
                {resumen && modo === 'procesar' && <Badge className="bg-cyan-100 text-cyan-800">Procesadas: {resumen.procesados_ok}</Badge>}
              </div>
              <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5">
                <button onClick={() => setVista('cards')} data-testid="coelsa-vista-cards"
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${vista === 'cards' ? 'bg-cyan-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
                  <LayoutGrid size={13} /> Tarjetas
                </button>
                <button onClick={() => setVista('lista')} data-testid="coelsa-vista-lista"
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${vista === 'lista' ? 'bg-cyan-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
                  <List size={13} /> Lista
                </button>
              </div>
            </div>
          )}

          {loading && items.length === 0 && (
            <Card className="p-10 bg-white text-center">
              <Loader2 size={28} className="mx-auto animate-spin text-cyan-500" />
              <p className="text-sm text-slate-500 mt-3">Conectando con Waya… {progress ? `${progress.procesados}/${progress.total}` : ''}</p>
            </Card>
          )}

          {!loading && items.length === 0 && (
            <Card className="p-10 bg-white text-center text-slate-400" data-testid="coelsa-empty">
              <ScanSearch size={36} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm">Pegá los Ids Coelsa y presioná "Validar" para ver el estado de las transacciones.</p>
            </Card>
          )}

          {items.length > 0 && (
            vista === 'lista'
              ? <CoelsaTable items={items} />
              : items.map((r, idx) => <ResultCard key={`${r.codigo}-${idx}`} r={r} idx={idx} />)
          )}
        </div>
      </div>
    </div>
  );
};

export default CoelsaTransacciones;
